import { Component, OnDestroy, OnInit, ElementRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ConsumerInteractionModel, ConsumerInteractionProgressType, ConsumerInteractionType } from '../../../models/consumer/consumer-interaction.model';
import { FriendlyDisconnectResponseModel } from '../../../models/consumer/friendly-disconnect-response.model';
import { ConsumerInteractionService } from '../../../services/consumer-interaction.service';
import { getInteractionType } from '../../../utilities/consumer-utilities';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import createAutoCorrectedDatePipe from 'text-mask-addons/dist/createAutoCorrectedDatePipe'
import * as moment from 'moment';
import swal from 'sweetalert2';

@Component({
    selector: 'friendly-disconnect-item',
    templateUrl: './friendly-disconnect-item.component.html',
    styles: []
})
export class FriendlyDisconnectItemComponent implements OnInit, OnDestroy {

    form: UntypedFormGroup;
    dateMask = [/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/];
    dateAutoCorrectPipe = createAutoCorrectedDatePipe('dd/mm/yyyy');
    isEditing: boolean = false;
    consumerId: string;
    clientId: number;
    subscriptions: any[] = [];
    showProgress: boolean = false;
    friendlyDisconnectInteraction: FriendlyDisconnectResponseModel;
    progress: ConsumerInteractionProgressType = ConsumerInteractionProgressType.InProgress;
    interactionType: ConsumerInteractionType = ConsumerInteractionType.GetFDSettings;
    isProcessing: boolean = false;
    intervalList: any[] = [];

    constructor(
        private fb: UntypedFormBuilder,
        private elRef: ElementRef,
        private route: ActivatedRoute,
        private consumerInteractionService: ConsumerInteractionService
    ) { }

    ngOnInit() {
        this.form = this.fb.group({
            'saturdayDisconnect': [],
            'saturdayStartTime': [],
            'saturdayEndTime': [],
            'sundayDisconnect': [],
            'sundayStartTime': [],
            'sundayEndTime': [],
            'weekdayDisconnect': [],
            'weekdayStartTime': [],
            'weekdayEndTime': [],
            'specialDays': [],
            'specialDay': []
        });

        this.clientId = this.route.parent.snapshot.params['clientId'];
        this.subscriptions.push(this.route
            .params
            .subscribe(params => {
                this.consumerId = params['consumerId'];
            }));

        this.consumerInteractionService.GetFriendlyDisconnect(this.clientId, this.consumerId).then(
            result => {
                this.friendlyDisconnectInteraction = this.formatFriendlyDisconnectTimes(result);
                this.form.setValue({
                    saturdayDisconnect: result.saturdayDisconnect,
                    saturdayStartTime: result.saturdayStartTime,
                    saturdayEndTime: result.saturdayEndTime,
                    sundayDisconnect: result.sundayDisconnect,
                    sundayStartTime: result.sundayStartTime,
                    sundayEndTime: result.sundayEndTime,
                    weekdayDisconnect: result.weekdayDisconnect,
                    weekdayStartTime: result.weekdayStartTime,
                    weekdayEndTime: result.weekdayEndTime,
                    specialDays: result.specialDays.map(date => {
                        return {
                            date: date,
                            selected: true
                        }
                    }),
                    specialDay: null
                });
            }
        ).catch((error) => {
            console.log("Could not get friendly disconnect settings: " + error);
        }) 

        this.consumerInteractionService.GetConsumerInteraction(this.clientId, this.consumerId, this.interactionType).then((interactionRes) => {
            let interaction = interactionRes as ConsumerInteractionModel[];

            if (!interaction) {
                this.showProgress = false;

            } else {
                this.showProgress = true;
                this.progress = getInteractionType(interaction);
                if (this.progress == ConsumerInteractionProgressType.InProgress) {
                    this.pollFriendlyInteraction();
                }
            }
        }).catch((error) => {
            this.progress = ConsumerInteractionProgressType.Error;
            this.showProgress = false;
        })
    }

    ngOnDestroy() {
        this.clearIntervalList();
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    onEditClick(isSubmit: boolean = false) {
        let show = () => {
            this.isEditing = true;
            this.elRef.nativeElement.parentElement.classList.add('module__edit');
        };

        let hide = (isReload?: boolean) => {
            if (isReload)  this.ngOnInit();
            this.isEditing = false;
            this.elRef.nativeElement.parentElement.classList.remove('module__edit');
        };

        // toggle/cancel
        if (!isSubmit) {
            this.isEditing = !this.isEditing;
            this.isEditing ? show() : hide();
            return;
        }

        if (isSubmit) {
            if (this.form.invalid || !this.form.dirty) return;

            swal({
                title: "Are you sure?",
                text: "You're about to make an adjustment to a consumer's friendly disconnect settings. Do you want to proceed?",
                confirmButtonText: 'Yes, please',
                confirmButtonColor: '#00539f',
                showCancelButton: true,
                cancelButtonText: "No, go back",
                cancelButtonColor: '#00539f',
            }).then(val => {
                if (val.value == true) {
                    let getSecondsFromMidnight = (time) => {
                        let seconds = 0;
                        seconds += time.split(':')[0] * 60 * 60;
                        seconds += time.split(':')[1] * 60;
                        return seconds;
                    }

                    let model = {
                        specialDays: this.form.value.specialDays.map(x => x.date),
                        weekdays: {
                            disconnection_allowed: this.form.value.weekdayDisconnect,
                            earliest_time: this.form.value.weekdayDisconnect ? getSecondsFromMidnight(this.form.value.weekdayStartTime) : 0,
                            latest_time: this.form.value.weekdayDisconnect ? getSecondsFromMidnight(this.form.value.weekdayEndTime) : 0
                        },
                        saturday: {
                            disconnection_allowed: this.form.value.saturdayDisconnect,
                            earliest_time: this.form.value.saturdayDisconnect ? getSecondsFromMidnight(this.form.value.saturdayStartTime) : 0,
                            latest_time: this.form.value.saturdayDisconnect ? getSecondsFromMidnight(this.form.value.saturdayEndTime) : 0
                        },
                        sunday: {
                            disconnection_allowed: this.form.value.sundayDisconnect,
                            earliest_time: this.form.value.sundayDisconnect ? getSecondsFromMidnight(this.form.value.sundayStartTime) : 0,
                            latest_time: this.form.value.sundayDisconnect ? getSecondsFromMidnight(this.form.value.sundayEndTime) : 0
                        }
                    }

                    this.consumerInteractionService.PostSetFriendlyDisconnectRequest(this.clientId, this.consumerId, model).subscribe(
                        result =>  hide(true)
                    );
                }
            });
        }
    }

    onClickInfo() {
        swal({
            title: 'Friendly disconnect settings',
            html: '<p style="text-align: left;" >Friendly disconnect settings allow you to set the device to only allow disconnection throughout certain times of the day i.e. so that ourside of these set-hours the device will not disconnect, for example unsociable hours or where your customers may not have access to payment services that are not available 24 hours a day.</p>',
            confirmButtonText: 'Close',
            confirmButtonColor: '#00539f'
        });
    }

    onSpecialDateClick(item: any) {
        let values = this.form.value.specialDays;
        values.find(x => item.date == x.date).selected = !item.selected;
        this.form.controls.specialDays.setValue(values);
    }

    onAddSpecialDateClick() {
        let m = moment(this.form.value.specialDay, 'DD/MM/YYYY');
        let isValid = m.isValid() && m.isSameOrAfter();
        if (!isValid) return;

        let values = this.form.value.specialDays;
        let date = m.format('YYYY-MM-DDTHH:mm:ss');
        if (values.find(x => x.date === date)) return;

        values.push({
            date: date,
            selected: true
        });

        this.form.controls.specialDays.setValue(values);
    }

    clearIntervalList() {
        for (let i = 0; i < this.intervalList.length; i++) {
            window.clearInterval(this.intervalList[i]);
        }

        for (let j = 0; j < 100; j++) {
            window.clearInterval(j);
        }

        this.intervalList = [];
    }

    pollFriendlyInteraction() {
        if (this.progress != ConsumerInteractionProgressType.InProgress) {
            this.isProcessing = true;
            this.clearIntervalList();
            this.getFriendlyDisconnect();
        }

        this.intervalList.push(window.setInterval(() => { this.getFriendlyDisconnect() }, 30000));
    }

    getFriendlyDisconnect() {
        if (this.progress != ConsumerInteractionProgressType.InProgress) {
            this.consumerInteractionService.PostFriendlyDisconnectRequest(this.clientId, this.consumerId).then((friendlyPostRes) => {

                this.consumerInteractionService.GetConsumerInteraction(this.clientId, this.consumerId, this.interactionType).then((interactionRes) => {
                    let interaction = interactionRes as ConsumerInteractionModel[];
                    if (!interaction) {
                        this.showProgress = false;
                        this.isProcessing = false;
                    } else {
                        this.showProgress = true;
                        this.isProcessing = false;
                        this.progress = getInteractionType(interaction);
                        if (this.progress != ConsumerInteractionProgressType.InProgress) {
                            this.clearIntervalList();
                        }
                    }
                }).catch((error) => {
                    this.progress = ConsumerInteractionProgressType.Error;
                    this.showProgress = false;
                    this.isProcessing = false;
                })

            }).catch((error) => {
                this.isProcessing = false;
            })
        } else {
            this.consumerInteractionService.GetConsumerInteraction(this.clientId, this.consumerId, this.interactionType).then((interactionRes) => {
                let interaction = interactionRes as ConsumerInteractionModel[];
                if (!interaction) {
                    this.showProgress = false;
                    this.isProcessing = false;
                } else {
                    this.showProgress = true;
                    this.isProcessing = false;
                    this.progress = getInteractionType(interaction);
                    if (this.progress != ConsumerInteractionProgressType.InProgress) {
                        this.clearIntervalList();
                    }
                }
            }).catch((error) => {
                this.progress = ConsumerInteractionProgressType.Error;
                this.showProgress = false;
                this.isProcessing = false;
            })
        }
    }

    formatTime24(data: string): string {
        // assuming hh:mm:ss 24 hour format to hh:mm pm/am
        if (!data) { return ""; }
        let timeType = "am"
        let timeParts = data.split(":");
        if (timeParts.length > 2) { timeParts.pop(); }

        if (Number(timeParts[0]) > 12 ||
            (Number(timeParts[0]) == 12 && Number(timeParts[1]) > 0)) {
            timeType = "pm"
        } else { timeType = "am" }

        let result = timeParts.join(":") + timeType;
        return result;
    }

    formatFriendlyDisconnectTimes(model: FriendlyDisconnectResponseModel): FriendlyDisconnectResponseModel {

        model.saturdayStartTime = this.formatTime24(model.saturdayStartTime);
        model.saturdayEndTime = this.formatTime24(model.saturdayEndTime);

        model.sundayStartTime = this.formatTime24(model.sundayStartTime);
        model.sundayEndTime = this.formatTime24(model.sundayEndTime);

        model.weekdayStartTime = this.formatTime24(model.weekdayStartTime);
        model.weekdayEndTime = this.formatTime24(model.weekdayEndTime);

        return model;
    }
}
